import { useEffect, useRef } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const TABS = [
  'home',
  'guests',
  'venues',
  'vendors',
  'budget',
  'seating',
  'timeline',
  'site',
] as const;
export type TabKey = (typeof TABS)[number];

/**
 * Tabs across the top on desktop; the same row scrolls horizontally on a phone.
 *
 * Eight tabs do not fit in 390px and never will, so the row scrolls — but a
 * scrolling row has a failure mode a fixed one does not: land on Timeline from a
 * deep link and the active tab is off-screen to the right, so the app looks like
 * it opened on Home. The effect below scrolls the active tab into view on every
 * navigation, which is the whole reason this component has a ref.
 */
export function TabBar({ wedId }: { wedId: string }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const railRef = useRef<HTMLUListElement>(null);

  useEffect(() => {
    const active = railRef.current?.querySelector('[aria-current="page"]');
    // `nearest` rather than `center`: on a wide screen every tab is already
    // visible and centring would scroll a row that did not need scrolling.
    active?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
  }, [pathname]);

  return (
    // Hidden on a phone, where `BottomNav` is the navigation. Kept rather than
    // made responsive: a row of eight and a bar of four are different designs,
    // and one component pretending to be both would serve neither.
    <nav aria-label="Planner sections" className="hidden border-b border-line bg-paper sm:block">
      <ul
        ref={railRef}
        className="rail gutter mx-auto flex w-full max-w-[1400px] gap-1 overflow-x-auto"
      >
        {TABS.map((tab) => (
          <li key={tab}>
            <NavLink
              to={`/app/w/${wedId}/${tab}`}
              className={({ isActive }) =>
                [
                  'inline-flex min-h-tap items-center whitespace-nowrap border-b-2 px-3 text-body',
                  isActive
                    ? 'border-brass font-medium text-ink'
                    : 'border-transparent text-leaf-soft hover:text-ink',
                ].join(' ')
              }
            >
              {t(`nav.${tab}`)}
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
}
